import { Completion } from '@codemirror/autocomplete';

import {
  AutocompleteSuggestion,
  AutocompleteSuggestionPart,
} from '@dynatrace-sdk/client-query';

import {
  convertToMiddleEllipsis,
  fuzzyMatch,
  getFuzzyMatchFullLabelPosition,
  labelShouldBeTruncated,
} from './fuzzy-match.js';
import { getSuggestions, type SuggestionCache } from './get-suggestions.js';
import { SuggestionPart } from './suggestion-popup/SuggestionPart.js';
import { getTokenTypeLabel } from './token-type-label.js';
import { combinedDurationUnits } from '../../../core/dql/dql-common.js';
import { Flex } from '../../../layouts/flex/Flex.js';
import { Text } from '../../../typography/text/Text.js';
import type {
  AutocompleteProvider,
  AutocompletionResult,
  Suggestion,
} from '../../base-code-editor/auto-completion/autocompletion.extension.js';
import { verifyQuery } from '../dql/dql-linter.js';

/**
 * Extracts the last word or dot-separated word from the content.
 * @param content - the content of the editor up to the cursor position.
 * @returns an object containing the raw captured word and the trimmed word without leading digits.
 */
function extractWordToken(content: string): {
  rawCapture: string;
  trimmedCapture: string;
} {
  const rawCapture = /\W?([\w.]*)$/.exec(content)?.[1] ?? '';
  // Strip any leading integer or decimal so the unit suffix (e.g. "h", "ms") is used
  // as the match pattern to ensure duration unit suggestions are shown correctly after a number.
  const trimmedCapture = rawCapture.replace(/^\d+(\.\d+)?/, '');

  return { rawCapture, trimmedCapture };
}

/**
 * Checks whether the user is typing a duration unit alias after a number.
 * @param rawCapture - the raw captured word token.
 * @param trimmedCapture - the captured word token with any leading numeric prefix removed.
 * @returns true if the user is typing a duration unit alias after a number, false otherwise.
 */
function getIsTypingUnitAlias(
  rawCapture: string,
  trimmedCapture: string,
): boolean {
  return (
    trimmedCapture.length > 0 &&
    trimmedCapture !== rawCapture &&
    combinedDurationUnits.some((unit) => unit.startsWith(trimmedCapture))
  );
}

type FilteredSuggestion = AutocompleteSuggestion & {
  score: number;
  startPos: number;
};

const TOKEN_TYPES = {
  ANCHOR: 'FIELD_VALUE',
  SYNOPSIS_INFO: 'SYNOPSIS_INFO',
} as const;

// Part types that can serve as a tooltip title (named entities, not separators).
const TITLE_TYPES = new Set([
  'COMMAND_NAME',
  'FUNCTION_NAME',
  'TIMESERIES_AGGREGATION',
]);

/**
 * Whether a suggestion part must be hidden from descriptions and popups.
 * Value anchors and command-level synopsis text are excluded from all
 * user-visible surfaces.
 */
function isHiddenPart(part: AutocompleteSuggestionPart): boolean {
  // Cast: not yet in the SDK's `type` union. See [APPDEV-19104].
  const type = part.type as string;
  return type === TOKEN_TYPES.ANCHOR || type === TOKEN_TYPES.SYNOPSIS_INFO;
}

/**
 * Builds the cursor-position description from the empty suggestions' parts.
 * Excludes value anchors and command-level synopsis; field-name info is kept.
 */
function getPositionDescription(
  emptySuggestionParts: AutocompleteSuggestionPart[],
): string {
  return emptySuggestionParts
    .filter((part) => !!part.info && !isHiddenPart(part))
    .map((part) => part.info as string)
    .join(' ');
}

/**
 * Filters out and trims quote suggestions.
 * @param suggestions - suggestions list
 */
function removeQuoteSuggestions(
  suggestions: FilteredSuggestion[],
): FilteredSuggestion[] {
  const excludedSuggestions = ['"', '""', '"""', ' '];
  return suggestions
    .filter((elem) => !excludedSuggestions.includes(elem.suggestion))
    .map((elem) => {
      // trim trailing quotation marks
      const trimmed = elem.suggestion.replace(/"{1,3}$/, '');
      return { ...elem, suggestion: trimmed };
    })
    .filter((elem) => elem.suggestion.length > 0);
}

/**
 *  Filters the suggestions based on matching pattern and suggestions as well as the
 *  autocomplete trigger pattern.
 *
 * @param suggestions - suggestions for the pattern
 * @param pattern - pattern matched
 * @param requestCursorPosition - the request cursor position
 * @param cursorPosition - editor cursor position
 * @param disableFilter - flag for whether the autocomplete was triggered via ctrl+space or having a pattern length of 1 or less
 */
function filterSuggestions(
  suggestions: AutocompleteSuggestion[],
  pattern: string,
  requestCursorPosition: number,
  cursorPosition: number,
  disableFilter: boolean,
): Array<FilteredSuggestion> {
  return suggestions
    .reduce<Array<FilteredSuggestion>>((result, suggestion) => {
      // Check if the suggestion matches part of the pattern
      const matchResult = fuzzyMatch(pattern, suggestion.suggestion);
      const match =
        suggestion.alreadyTypedCharacters === 0 ? true : matchResult.match;
      // Give the match a score for sorting the suggestions based on the closest match to the pattern
      const score =
        suggestion.alreadyTypedCharacters === 0 ? 1 : matchResult.score;
      const startPos =
        suggestion.alreadyTypedCharacters === 0
          ? cursorPosition
          : requestCursorPosition - suggestion.alreadyTypedCharacters;
      if (suggestion.suggestion.length > 0 && (disableFilter || match)) {
        result.push({ ...suggestion, score, startPos });
      }
      return result;
    }, [])
    .sort((a, b) => b.score - a.score);
}

/**
 * Builds the query string sent to the autocomplete backend.
 * @param autocompleteContent - editor content up to the cursor position
 * @param patternLength - length of the full typed pattern
 * @param requestLength - how many characters of the pattern to include; use 1 to maximise
 *   cache hits, or `patternLength` for explicit (Ctrl+Space) completions
 */
function getRequestString(
  autocompleteContent: string,
  patternLength: number,
  requestLength: number,
) {
  return autocompleteContent.slice(
    0,
    autocompleteContent.length - patternLength + requestLength,
  );
}

// max. number of suggestions fetched from autocomplete endpoint
const MAX_DATA_SUGGESTIONS = 10_000;

// Usable horizontal space in the autocomplete dropdown (476px with scrollbar minus 2×12px padding).
const AUTOCOMPLETE_PANEL_USABLE_PX = 452;
// Approximate pixels per character for the suggestion label (code font, ~14px).
const CODE_FONT_PX_PER_CHAR = 9;
// Approximate pixels per character for the type label (text-small font, ~12px).
const TYPE_LABEL_PX_PER_CHAR = 7;
// Gap between suggestion label and type label — matches the flex gap on the list item (Spacings.Size4).
const LABEL_GAP_PX = 4;
// Minimum label length to keep suggestions readable even when the type label is very long.
const MIN_LABEL_LENGTH_WITH_TYPE = 20;

/**
 * Estimates the maximum character length for a suggestion label when a type label is shown
 * alongside it. Uses approximate per-character pixel widths (code font ≈9px, text-small ≈7px)
 * against {@link AUTOCOMPLETE_PANEL_USABLE_PX}. Floored at {@link MIN_LABEL_LENGTH_WITH_TYPE}
 * to keep suggestions readable even when the type label is very long.
 * @param typeLabelLength - character length of the type label (e.g. "Metric key" → 10)
 */
function getLabelMaxLengthWithType(typeLabelLength: number): number {
  // Pixels consumed by the type label and the gap between label and type label.
  const reservedPx = typeLabelLength * TYPE_LABEL_PX_PER_CHAR + LABEL_GAP_PX;
  // Remaining pixels available for the suggestion label, converted to character count.
  const availableChars = Math.floor(
    (AUTOCOMPLETE_PANEL_USABLE_PX - reservedPx) / CODE_FONT_PX_PER_CHAR,
  );
  return Math.max(MIN_LABEL_LENGTH_WITH_TYPE, availableChars);
}

/**
 * Fetches suggestions from the backend and filters them against the current pattern.
 * @param autocompleteContent - editor content up to the cursor position
 * @param patternLength - length of the typed pattern used for filtering
 * @param explicit - when true (Ctrl+Space), the full pattern is sent to the backend so all
 *   matching suggestions are returned; when false, only 1 character is sent to maximise cache hits
 * @param cache - per-editor suggestion cache
 */
async function getFilteredSuggestions(
  autocompleteContent: string,
  patternLength: number,
  explicit: boolean,
  cache: SuggestionCache,
): Promise<FilteredSuggestion[]> {
  const requestString = getRequestString(
    autocompleteContent,
    patternLength,
    explicit ? patternLength : 1,
  );
  const suggestions = await getSuggestions(
    requestString,
    requestString.length,
    MAX_DATA_SUGGESTIONS,
    cache,
  );

  const pattern = autocompleteContent.slice(
    autocompleteContent.length - patternLength,
  );
  return filterSuggestions(
    suggestions,
    pattern,
    requestString.length,
    autocompleteContent.length,
    explicit,
  );
}

/**
 * Derives the tooltip title for a suggestion by finding the first part that
 * produces a token type label, then combining that part's suggestion text
 * (capitalized) with the lower-cased label (e.g. part `"arrayAvg"` + `"Function"` → `"ArrayAvg function"`).
 * @param suggestion - the suggestion to derive the title for.
 * @returns the derived title string, or `undefined` when no part produces a token type label.
 */
export function getSuggestionTitle(
  suggestion: FilteredSuggestion,
): string | undefined {
  for (const part of suggestion.parts) {
    if (!TITLE_TYPES.has(part.type)) {
      continue;
    }
    const label = getTokenTypeLabel([part]);
    if (label !== undefined) {
      const { suggestion: text } = part;
      return `${text.charAt(0).toUpperCase()}${text.slice(
        1,
      )} ${label.toLowerCase()}`;
    }
  }
}

/**
 * Builds the popup render function for a suggestion's info parts (anchors
 * excluded), or `undefined` when there is no info to show.
 * @param suggestion - the suggestion whose info parts should be rendered.
 * @returns a popup render function, or `undefined` when there is no info.
 */
function buildSuggestionInfoPopup(
  suggestion: FilteredSuggestion,
): Suggestion['popup'] {
  const infoParts = suggestion.parts.filter(
    (part) => !!part.info && !isHiddenPart(part),
  );
  if (infoParts.length === 0) {
    return undefined;
  }
  const title = getSuggestionTitle(suggestion);
  if (infoParts.length === 1) {
    return () => <SuggestionPart suggestionPart={infoParts[0]} title={title} />;
  }
  const [firstPart, ...remainingParts] = infoParts;
  return () => (
    <Flex flexDirection="column" gap={16}>
      <SuggestionPart suggestionPart={firstPart} title={title} />
      {remainingParts.map((part) => (
        <SuggestionPart key={part.info} suggestionPart={part} />
      ))}
    </Flex>
  );
}

/**
 * Builds the popup for an autocomplete suggestion.
 *
 * @param label - the full (non-truncated) suggestion label.
 * @param truncated - whether the label was shortened with a middle ellipsis.
 * @param infoPopup - the pre-built info popup for this suggestion, or `undefined` if there is none.
 * @returns a popup render function, or `undefined`.
 */
function buildSuggestionPopup(
  label: string,
  truncated: boolean,
  infoPopup: Suggestion['popup'],
): Suggestion['popup'] {
  if (label.length === 0) {
    return undefined;
  }
  if (truncated) {
    return () => (
      <>
        <Text fontStyle="code" as="p">
          {label}
        </Text>
        {infoPopup ?? null}
      </>
    );
  }
  return infoPopup;
}

/**
 * Creates the DQL autocomplete provider bound to a `cache` owned by the calling
 * editor instance. Keeping the cache per-instance ensures suggestions never
 * outlive the editor or leak between editors (or between dev-app stories).
 * @param cache - the suggestion cache owned by the editor instance.
 */
export const createSuggestionAutocompletion =
  (cache: SuggestionCache): AutocompleteProvider =>
  async (context): Promise<AutocompletionResult | null> => {
    if (context.matchBefore(/[^\w.]/) && !context.explicit) {
      return null;
    }
    // For performance, only take the query up until the cursor position.
    // This allows the backend to be queried less often and the cache more often
    const autocompleteContent = context.state.doc
      .slice(0, context.pos)
      .toString();
    const { rawCapture, trimmedCapture: pattern } =
      extractWordToken(autocompleteContent);

    const infoSuggestions = await getSuggestions(
      getRequestString(autocompleteContent, pattern.length, 0),
      undefined,
      undefined,
      cache,
    );

    let filteredSuggestions = await getFilteredSuggestions(
      autocompleteContent,
      pattern.length,
      pattern.length <= 1 ||
        getIsTypingUnitAlias(rawCapture, pattern) ||
        pattern.endsWith('.'),
      cache,
    );

    if (filteredSuggestions.length === 0) {
      if (pattern.length > 1) {
        filteredSuggestions = await getFilteredSuggestions(
          autocompleteContent,
          pattern.length,
          true,
          cache,
        );
      }
    }

    // Filter out suggestions (mainly quotes) if the cursor is within a string token and
    // the autocomplete query was not the full query (there are characters after the cursor).
    // This is necessary as we only send the autocomplete request with the query up to the cursor,
    // omitting text after the cursor (if present).
    // We still do want to show quotes if the cursor is at the end of the text and even within a string token.
    if (
      context.tokenBefore(['string']) !== null &&
      autocompleteContent.length !== context.state.doc.length
    ) {
      filteredSuggestions = removeQuoteSuggestions(filteredSuggestions);
    }

    const emptySuggestionParts = infoSuggestions
      .filter((suggestion) => !suggestion.suggestion)
      .flatMap((suggestion) => suggestion.parts);

    const section = getPositionDescription(emptySuggestionParts);

    /*
     * If there are no autocomplete suggestions but there is a position
     * description to show, we still surface it as a "section header". CodeMirror
     * does not allow sections without items, so we push one empty (hidden)
     * suggestion to carry it. When the description is empty (e.g. only value
     * anchors are present), we skip this so the popup does not open empty.
     */
    if (filteredSuggestions.length === 0 && section) {
      const emptySuggestion = infoSuggestions.find(
        (s) => s.suggestion.length === 0,
      );
      if (emptySuggestion) {
        filteredSuggestions.push({
          ...emptySuggestion,
          score: -99,
          startPos: context.pos,
        });
      }
    }

    if (filteredSuggestions.length === 0) {
      return null;
    }

    const autocompletePos = Math.min(
      ...filteredSuggestions.map((s) => s.startPos),
    );

    return {
      suggestions: filteredSuggestions.map((suggestion): Suggestion => {
        const infoPopup = buildSuggestionInfoPopup(suggestion);
        const tokenTypeLabel = getTokenTypeLabel(suggestion.parts);

        const effectiveMaxLength = tokenTypeLabel
          ? getLabelMaxLengthWithType(tokenTypeLabel.length)
          : undefined;
        const shouldBeTruncated = labelShouldBeTruncated(
          suggestion.suggestion,
          effectiveMaxLength,
        );
        const middleEllipsisText = shouldBeTruncated
          ? convertToMiddleEllipsis(suggestion.suggestion, effectiveMaxLength)
          : suggestion.suggestion;

        return {
          ...suggestion,
          label: middleEllipsisText,
          section,
          postfix: tokenTypeLabel,
          apply: (view) => {
            view.dispatch({
              changes: {
                from: suggestion.startPos,
                to: autocompleteContent.length,
                insert: suggestion.suggestion,
              },
              selection: {
                anchor: suggestion.startPos + suggestion.suggestion.length,
              },
              userEvent: 'input.complete',
            });
          },

          popup: buildSuggestionPopup(
            suggestion.suggestion,
            shouldBeTruncated,
            infoPopup,
          ),
        };
      }),
      from: autocompletePos,
      async reopen(completion: Completion & Partial<FilteredSuggestion>) {
        const queryToEnd = context.state.doc.slice(context.pos).toString();

        // Heuristic: if non-whitespace content exists to the right of the cursor,
        // treat this as inline token editing and skip reopen to avoid noisy popup
        // behavior while the user is fixing text in place.
        if (queryToEnd.trim().length > 0) {
          return false;
        }

        const completedQuery =
          autocompleteContent.slice(0, completion.startPos) +
          completion.suggestion +
          queryToEnd;

        const { valid = true } = (await verifyQuery(completedQuery)) ?? {};
        return !valid;
      },
      filter: false,
      getMatch: (completion: Completion & Partial<FilteredSuggestion>) => {
        const completionTypeLabel =
          completion.parts && getTokenTypeLabel(completion.parts);
        const matchMaxLength = completionTypeLabel
          ? getLabelMaxLengthWithType(completionTypeLabel.length)
          : undefined;
        return (
          getFuzzyMatchFullLabelPosition(
            pattern,
            completion.suggestion,
            matchMaxLength,
          ) ??
          fuzzyMatch(
            autocompleteContent.slice(autocompletePos),
            completion.suggestion ?? '',
          ).pos ??
          []
        );
      },
    };
  };
